import './token-selector.css';

import { useState, useMemo, useRef, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Search, Lock } from 'lucide-react';

import { TokenConfirmModal } from './token-modal-confirm';
import {
  getLayout,
  useLeague,
  useLeagueSeasonTimer,
  useTokenSelector,
} from '../../../hooks';
import { Spin } from '../../spin';
import { WinningTokens } from './winning-tokens';
import { ModeLeagueType } from '../../../leagues';

interface TokenSelectorProps {
  maxTokens: number;
  selectedTokens: number[];
  onSelectToken: (token: number, mode: 'main' | 'arena') => void;
  availableSelections: number;
  mode?: ModeLeagueType;
}

export function TokenSelector({
  maxTokens,
  selectedTokens,
  onSelectToken,
  availableSelections,
  mode = 'main',
}: TokenSelectorProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  const { timeRemaining } = useLeagueSeasonTimer();
  const finished = timeRemaining.finished;

  const { league, loading } = useLeague();
  const { tokens } = useTokenSelector();

  const [pendingToken, setPendingToken] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [layout, setLayout] = useState(() =>
    getLayout(typeof window !== 'undefined' ? window.innerWidth : 1024),
  );

  useEffect(() => {
    const onResize = () => setLayout(getLayout(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const allTokens = useMemo(
    () => Array.from({ length: maxTokens }, (_, i) => i + 1),
    [maxTokens],
  );

  const filteredTokens = useMemo(() => {
    if (!searchTerm.trim()) return allTokens;
    return allTokens.filter((t) => t.toString().includes(searchTerm.trim()));
  }, [searchTerm, allTokens]);

  const occupiedTokens = useMemo(() => {
    return new Set(Object.values(tokens).map((t) => t.idToken));
  }, [tokens]);

  const rows = useMemo(() => {
    const res: number[][] = [];

    for (let i = 0; i < filteredTokens.length; i += layout.columns) {
      res.push(filteredTokens.slice(i, i + layout.columns));
    }

    return res;
  }, [filteredTokens, layout.columns]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => layout.size + 12,
    overscan: 8,
  });

  const isUserSelected = (t: number) => selectedTokens.includes(t);
  const isOccupied = (t: number) => occupiedTokens.has(t);

  const canSelect = (t: number) =>
    availableSelections > 0 && !isUserSelected(t) && !isOccupied(t);

  return (
    <div className="isg-token-selector">
      <TokenConfirmModal
        isOpen={pendingToken !== null}
        close={() => {}}
        tokenNumber={pendingToken || 0}
        onConfirm={() => {
          if (pendingToken != null) onSelectToken(pendingToken, mode);
          setPendingToken(null);
        }}
        onCancel={() => setPendingToken(null)}
        leagueName={league?.name}
        mode={mode}
      />

      <div className="isg-token-selector-header">
        {loading ? (
          <Spin />
        ) : !finished ? (
          <>
            <h2 className="isg-token-selector-title">
              {mode === 'arena'
                ? 'Seleccionar Token Arena'
                : 'Seleccionar Token Único'}
            </h2>

            <p className="isg-token-selector-subtitle">
              Elige el número de token que deseas para tu tarjeta.
            </p>
          </>
        ) : (
          <WinningTokens mode={mode} />
        )}
      </div>

      {!loading && !finished && (
        <>
          <div className="isg-token-selector-search">
            <div className="isg-token-selector-search-wrapper">
              <Search className="isg-token-selector-search-icon" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="isg-token-selector-input"
                placeholder="Buscar token..."
              />
            </div>
          </div>

          <div
            ref={parentRef}
            className="isg-token-selector-scroll custom-scrollbar"
            style={{ height: 600, overflow: 'auto' }}
          >
            <div
              style={{
                height: virtualizer.getTotalSize(),
                position: 'relative',
                width: '100%',
              }}
            >
              {virtualizer.getVirtualItems().map((row) => {
                const rowTokens = rows[row.index];

                return (
                  <div
                    key={row.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${row.start}px)`,

                      display: 'grid',
                      gridTemplateColumns: `repeat(${layout.columns}, ${layout.size}px)`,
                      gap: '10px',
                      padding: '8px',
                      boxSizing: 'border-box',
                    }}
                  >
                    {rowTokens.map((token) => {
                      const userSelected = isUserSelected(token);
                      const occupied = isOccupied(token);
                      const selectable = canSelect(token);

                      return (
                        <button
                          key={token}
                          onClick={() => selectable && setPendingToken(token)}
                          disabled={!selectable}
                          className={`isg-token-btn
                            ${userSelected ? 'selected' : occupied ? 'occupied' : !selectable ? 'disabled' : 'active'}`}
                          style={{
                            width: layout.size,
                            height: layout.size,
                          }}
                        >
                          {(userSelected || occupied) && (
                            <Lock className="isg-token-lock" />
                          )}
                          {token}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {availableSelections === 0 && selectedTokens.length === 0 && (
        <div className="isg-token-selector-empty">
          Compra tarjetas para poder seleccionar tokens
        </div>
      )}
    </div>
  );
}
